import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../lib/cart";
import { analytics, toItem } from "../lib/analytics";
import { pickLocale, formatAZN } from "../lib/format";
import { PillCTA } from "../components/atelier/primitives";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { productImage } from "../components/storefront/assets";
import AdfImage from "../components/storefront/AdfImage";
import { useI18n } from "../lib/i18n";
import { apiErrorMessage } from "../lib/apiErrors";
import { useDocumentTitle } from "../lib/useDocumentTitle";

export default function CartPage() {
  const { cart, patchItem, removeItem, applyPromotion, removePromotion, applyGiftCard, removeGiftCard } = useCart();
  const navigate = useNavigate();
  const { t } = useI18n();
  useDocumentTitle(t("cart.doc_title", "Səbət"));
  const [promoCode, setPromoCode] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  const [giftBusy, setGiftBusy] = useState(false);
  const [message, setMessage] = useState("");

  const items = cart?.items || [];
  const empty = items.length === 0;
  const appliedCodes = cart?.applied_promotion_codes || [];
  const appliedGiftCards = cart?.applied_gift_card_codes || [];

  const runCartAction = async (fn) => {
    setMessage("");
    try {
      await fn();
    } catch (err) {
      setMessage(apiErrorMessage(err, t));
    }
  };

  const applyCode = async (e) => {
    e.preventDefault();
    const code = promoCode.trim();
    if (!code) {
      setMessage(t("cart.promo.enter", "Voucher və ya promo kodu daxil edin."));
      return;
    }
    setPromoBusy(true);
    setMessage("");
    try {
      await applyPromotion(code);
      setPromoCode("");
    } catch (err) {
      setMessage(apiErrorMessage(err, t));
    } finally {
      setPromoBusy(false);
    }
  };

  const applyGift = async (e) => {
    e.preventDefault();
    const code = giftCode.trim();
    if (!code) {
      setMessage(t("cart.gift.enter", "Hədiyyə kartı kodunu daxil edin."));
      return;
    }
    setGiftBusy(true);
    setMessage("");
    try {
      await applyGiftCard(code);
      setGiftCode("");
    } catch (err) {
      setMessage(apiErrorMessage(err, t));
    } finally {
      setGiftBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-graphite" data-testid="cart-page">
      <Header />
      <main id="main" className="mx-auto max-w-[1240px] px-5 pb-20 pt-10 sm:px-8 lg:px-10">
        <section>
          <h1 className="font-serif text-[34px] leading-none sm:text-[40px]">{t("cart.title", "Səbət")}</h1>
          {!empty && (
            <p className="mt-3 text-sm text-graphite/60">
              {t("cart.count", "{count} məhsul", { count: items.length })}
            </p>
          )}
        </section>

      {empty ? (
        // An empty cart shows only the empty state — no summary, voucher, total
        // or checkout button.
        <section className="mx-auto flex max-w-md flex-col items-center px-6 py-20 text-center" data-testid="cart-empty">
          <p className="font-serif text-3xl text-graphite/80">{t("cart.empty.title", "Səbətiniz boşdur.")}</p>
          <p className="mt-3 font-light text-graphite/70">{t("cart.empty.body", "Kolleksiyadan bəyəndiyiniz parçaları seçərək səbətə əlavə edin.")}</p>
          <PillCTA as={Link} to="/shop" variant="primary" className="mt-8" data-testid="cart-empty-cta">
            {t("cart.empty.cta", "Kolleksiyaya bax")}
          </PillCTA>
        </section>
      ) : (
      <section className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <div className="border-t border-graphite/15" data-testid="cart-items">
          {
            items.map((it, idx) => {
              const stock = typeof it.available_stock === "number" ? it.available_stock : null;
              const atStockLimit = !it.allow_backorder && stock !== null && it.quantity >= stock;
              const gift = it.gift_meta || null;
              const isGiftCard = it.product_handle === "hediyye-karti";
              return (
              <div key={it.id} className="relative grid grid-cols-[96px_1fr] gap-5 border-b border-graphite/10 py-6 sm:grid-cols-[120px_1fr]" data-testid={`cart-item-${idx}`}>
                <Link to={`/products/${it.product_handle}`}>
                  <div className="aspect-[3/4] overflow-hidden bg-stone/30">
                    <AdfImage
                      source={it.thumbnail_url || productImage(idx)}
                      sizes="(min-width: 640px) 120px, 96px"
                      className="h-full w-full object-cover"
                    />
                  </div>
                </Link>
                <div className="min-w-0 pr-10">
                  <div className="text-[15px] font-medium leading-snug">{pickLocale(it.product_title)}</div>
                  <div className="mt-2 text-[12px] uppercase tracking-[0.08em] text-graphite/70">
                    {/* The bare variant name: "Qara" is a colour and "300 AZN"
                        an amount — the old "Ölçü" prefix misnamed both. */}
                    {it.variant_title}
                  </div>

                  {/* Quantity lives with the line it counts. */}
                  <div className="mt-3 flex items-center gap-3">
                    <span className="text-[12px] uppercase tracking-[0.08em] text-graphite/50">{t("cart.item.qty", "Say")}:</span>
                    <div className="flex items-center border border-graphite/20">
                      <button data-testid={`qty-dec-${idx}`} onClick={() => runCartAction(() => patchItem(it.id, Math.max(0, it.quantity - 1)))} className="h-9 w-9 text-base transition hover:bg-graphite hover:text-white">−</button>
                      <div className="flex h-9 w-9 items-center justify-center text-sm" data-testid={`qty-value-${idx}`}>{it.quantity}</div>
                      {/* Disabled at the stock ceiling instead of letting the
                          backend refuse with a 409 the shopper never sees. */}
                      <button
                        data-testid={`qty-inc-${idx}`}
                        disabled={atStockLimit}
                        title={atStockLimit ? t("cart.item.max_stock", "Bu ölçüdə mövcud olan son ədəddir.") : undefined}
                        onClick={() => runCartAction(() => patchItem(it.id, it.quantity + 1))}
                        className="h-9 w-9 text-base transition hover:bg-graphite hover:text-white disabled:pointer-events-none disabled:opacity-35"
                      >+</button>
                    </div>
                  </div>
                  {atStockLimit && (
                    <p className="mt-2 text-xs text-espresso" data-testid={`stock-note-${idx}`}>
                      {t("cart.item.max_stock", "Bu ölçüdə mövcud olan son ədəddir.")}
                    </p>
                  )}

                  <div className="mt-4 text-[15px] font-medium tracking-wide">
                    {formatAZN(it.total)}
                    {it.quantity > 1 && (
                      <span className="ml-2 text-xs font-normal text-graphite/50">{formatAZN(it.unit_price)} · {t("cart.item.each", "ədəd")}</span>
                    )}
                  </div>

                  {/* The recipient was entered on the gift page; show it back
                      so it can be checked and changed. */}
                  {isGiftCard && (
                    <div className="mt-4 border border-graphite/15 px-3 py-2 text-sm" data-testid={`cart-gift-meta-${idx}`}>
                      {gift?.recipient_email || gift?.recipient_name ? (
                        <>
                          <div className="text-graphite">
                            {t("cart.gift.for", "Alıcı")}: {gift.recipient_name || gift.recipient_email}
                          </div>
                          {gift.recipient_name && gift.recipient_email && (
                            <div className="text-graphite/65">{gift.recipient_email}</div>
                          )}
                          {gift.message && (
                            <div className="mt-1 italic text-graphite/65">“{gift.message}”</div>
                          )}
                        </>
                      ) : (
                        <div className="text-graphite/70">
                          {t("cart.gift.to_buyer", "Alıcı göstərilməyib — kod sizin e-poçtunuza göndəriləcək.")}
                        </div>
                      )}
                      <Link
                        to="/gift-card"
                        onClick={() => runCartAction(() => removeItem(it.id))}
                        className="mt-2 inline-block text-sm text-espresso underline underline-offset-4 hover:text-graphite"
                        data-testid={`cart-gift-edit-${idx}`}
                      >
                        {t("cart.gift.edit", "Məbləği və alıcını dəyiş")}
                      </Link>
                    </div>
                  )}
                </div>
                {/* The reference removes a line with a bare ×, top right. */}
                <button
                  data-testid={`remove-${idx}`}
                  aria-label={t("cart.item.remove", "Sil")}
                  onClick={() => runCartAction(() => removeItem(it.id))}
                  className="absolute right-0 top-6 flex h-10 w-10 items-center justify-center text-lg text-graphite/70 transition hover:text-graphite"
                >
                  ×
                </button>
              </div>
              );
            })
          }
        </div>

        <aside>
          <div className="lg:sticky lg:top-28" data-testid="cart-summary">
            <h2 className="font-serif text-xl">{t("cart.summary.title", "Sifarişin yekunu")}</h2>
            <hr className="mt-4 border-graphite/15" />
            {message && (
              <div className="mt-4 border border-burgundy/30 bg-burgundy/5 px-4 py-3 text-sm text-burgundy" role="alert" data-testid="cart-message">
                {message}
              </div>
            )}
            <div className="mt-5 space-y-4">
              <SummaryRow label={t("cart.subtotal_caps", "Cəmi")} value={formatAZN(cart?.subtotal || 0)} testid="cart-subtotal" />
              {((cart?.discount_total || 0) - (cart?.tier_discount_minor || 0)) > 0 && (
                <SummaryRow label={t("cart.discount", "Endirim")} value={`− ${formatAZN((cart.discount_total || 0) - (cart.tier_discount_minor || 0))}`} testid="cart-discount" />
              )}
              {(cart?.tier_discount_minor || 0) > 0 && (
                <SummaryRow label={t("cart.tier_discount", "Falcon üzvlük endirimi")} value={`− ${formatAZN(cart.tier_discount_minor)}`} testid="cart-tier-discount" />
              )}
              {(cart?.gift_card_total || 0) > 0 && (
                <SummaryRow label={t("cart.gift.applied", "Hədiyyə kartı")} value={`− ${formatAZN(cart.gift_card_total)}`} testid="cart-gift-total" />
              )}
              {/* The delivery method is chosen at checkout, so the basket must
                  not commit to a fee before the shopper has picked one — the
                  pre-quoted default read as "10 ₼ added without choosing". Shown
                  as calculated-at-checkout here; the real fee applies there. */}
              <SummaryRow
                label={t("cart.shipping", "Çatdırılma")}
                value={t("cart.shipping.at_checkout", "Ödənişdə hesablanır")}
                testid="cart-shipping"
              />
            </div>

            <form onSubmit={applyCode} className="mt-6" data-testid="cart-promo-form">
              <label className="block">
                <span className="text-[12px] uppercase tracking-[0.14em] text-graphite">{t("cart.promo.label_caps", "Promo kod")}</span>
                <div className="mt-2 flex min-h-[48px] border border-graphite/20 bg-white">
                  <input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder={t("cart.promo.placeholder", "Promo kod")}
                    className="min-w-0 flex-1 bg-transparent px-3 text-sm placeholder:text-graphite/35 focus:outline-none"
                    data-testid="cart-promo-code"
                  />
                  <button
                    type="submit"
                    disabled={promoBusy}
                    className="px-4 text-sm font-medium text-espresso hover:text-graphite disabled:opacity-40"
                    data-testid="cart-promo-apply"
                  >
                    {promoBusy ? "…" : t("cart.promo.apply", "Tətbiq et")}
                  </button>
                </div>
              </label>
              {appliedCodes.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="cart-applied-promos">
                  {appliedCodes.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => runCartAction(() => removePromotion(code))}
                      className="min-h-[36px] border border-espresso/40 px-3 text-xs text-graphite hover:bg-espresso/10"
                    >
                      {code} ×
                    </button>
                  ))}
                </div>
              )}
            </form>

            {/* Separate from the promo field above: a promo code is a discount
                the shop grants, a gift card is money the shopper already owns. */}
            <form onSubmit={applyGift} className="mt-5" data-testid="cart-gift-form">
              <label className="block">
                <span className="text-[12px] uppercase tracking-[0.14em] text-graphite">{t("cart.gift.label_caps", "Hədiyyə kartı kodu")}</span>
                <div className="mt-2 flex min-h-[48px] border border-graphite/20 bg-white">
                  <input
                    value={giftCode}
                    onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                    placeholder="ADF-XXXX-XXXX-XXXX"
                    className="min-w-0 flex-1 bg-transparent px-3 text-sm placeholder:text-graphite/35 focus:outline-none"
                    data-testid="cart-gift-code"
                  />
                  <button
                    type="submit"
                    disabled={giftBusy}
                    className="px-4 text-sm font-medium text-espresso hover:text-graphite disabled:opacity-40"
                    data-testid="cart-gift-apply"
                  >
                    {giftBusy ? "…" : t("cart.gift.apply", "Tətbiq et")}
                  </button>
                </div>
              </label>
              {appliedGiftCards.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="cart-applied-gift-cards">
                  {appliedGiftCards.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => runCartAction(() => removeGiftCard(code))}
                      className="min-h-[36px] border border-espresso/40 px-3 text-xs text-graphite hover:bg-espresso/10"
                    >
                      {code} ×
                    </button>
                  ))}
                </div>
              )}
            </form>

            <hr className="mt-6 border-graphite/15" />
            <div className="mt-5 flex items-baseline justify-between">
              <span className="text-[13px] uppercase tracking-[0.14em]">{t("cart.total_caps", "Yekun")}</span>
              <span className="text-lg font-semibold tracking-wide" data-testid="cart-total">{formatAZN((cart?.total || 0) - (cart?.shipping_total || 0))}</span>
            </div>
            {(cart?.points_to_earn || 0) > 0 && (
              <div className="mt-3 flex items-center gap-2 border border-espresso/25 bg-espresso/5 px-3 py-2 text-[13px] text-espresso" data-testid="cart-points-earn">
                <span aria-hidden="true">✦</span>
                <span>{t("cart.points_earn", "Bu sifarişlə {n} Falcon xalı qazanacaqsınız.", { n: cart.points_to_earn })}</span>
              </div>
            )}
            <button
              type="button"
              data-testid="cart-checkout"
              disabled={empty}
              className="mt-6 min-h-[56px] w-full bg-graphite text-[14px] font-medium uppercase tracking-[0.12em] text-white transition hover:bg-espresso disabled:opacity-40"
              onClick={() => {
                analytics.beginCheckout(
                  (cart?.items || []).map((line) => toItem(line.product || line, {
                    quantity: line.quantity,
                    item_variant: line.variant_title,
                  })).filter(Boolean),
                  cart?.total,
                );
                navigate("/checkout");
              }}
            >
              {t("cart.checkout_caps", "Sifarişi tamamla")}
            </button>
          </div>
        </aside>
      </section>
      )}
      </main>
      <Footer />
    </div>
  );
}

function SummaryRow({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-4" data-testid={testid}>
      <span className="text-[13px] uppercase tracking-[0.14em] text-graphite/80">{label}</span>
      <span className="text-[13px] uppercase tracking-[0.08em]">{value}</span>
    </div>
  );
}
